import React, { useState, useEffect, useRef } from 'react'
import { bool, number, oneOfType, shape, string } from 'prop-types'
import { trackEvent } from '@fs/zion-analytics'
import { TheaterOverlay, useOverlay, themes, Theme } from '@fs/zion-ui'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import animations from '../../../shared/services/cssAnimations'
import TileContent from './TileContent'
import TileText from './TileText'
import AncestorDetails from './TileContent/AncestorDetails/AncestorDetails'
import { useTileVisibility } from '../hooks/hooks'

const tileContentWrapperCss = css`
  height: 100%;
  & > div {
    height: 100%;
  }
`

export default function Tile({ tileData, color, autoload, index }) {
  const overlay = useOverlay()
  const { person } = tileData
  const tileRef = useRef()
  const isBelowTheFold = useTileVisibility(tileRef)
  const [t] = useTranslation()
  const helpActionUrl = '/discovery/about'
  const [transitioned, setTransitioned] = useState(false)

  const [openAncestorDetails, setOpenAncestorDetails] = useState(false)
  const { isOpen, transitioning } = overlay
  const params = new URLSearchParams(window.location.search)

  useEffect(() => {
    if (!isOpen && transitioning) {
      params.delete('experience')
      window.history.replaceState({}, '', decodeURIComponent(`${window.location.pathname}?${params}`))
    }
  }, [isOpen, params, transitioning])

  const masonryItemCss = css`
    cursor: pointer;
    display: inline-block;
    text-align: center;
    background-color: ${color};
    height: 100%;
    border-radius: 8px;
    border: none;
    opacity: 0;
    &:hover {
      box-shadow: 0px 1px 20px 0px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14),
        0px 3px 1px -2px rgba(0, 0, 0, 0.12);
    }
  `

  const showAnimation = css`
    animation: ${animations('zoomInDown')} ${index * 100}ms ease-in forwards;
    opacity: 1;
  `
  useEffect(() => {
    if (isOpen && !transitioning) {
      setTransitioned(true)
    }
    if (!isOpen && !transitioning && transitioned) {
      trackEvent({
        action: 'click_action',
        link_name: `DS_ALL_ABOUT_ME: DS_AAM_CLOSE_${tileData?.supportedTileId?.toUpperCase()}`,
      })
      setTransitioned(false)
    }
  }, [isOpen, transitioning, setTransitioned, transitioned, tileData.supportedTileId])

  const handleClick = (tile) => {
    if (tile.type === 'ancestorDetails') {
      setOpenAncestorDetails(true)
    } else {
      overlay.open()
    }
    params.set('experience', `${tile.title}+${tile.id}`)
    window.history.replaceState({}, '', decodeURIComponent(`${window.location.pathname}?${params}`))
  }
  const autoloader = () => {
    if (autoload) {
      overlay.open()
    } else {
      params.delete('experience')
      window.history.replaceState({}, '', decodeURIComponent(`${window.location.pathname}?${params}`))
    }
  }
  useEffect(autoloader, [])

  return (
    <>
      <button
        type="button"
        css={[masonryItemCss, !isBelowTheFold && showAnimation]}
        key={tileData.id}
        onClick={() => {
          trackEvent({
            action: 'click_action',
            link_name: `DS_ALL_ABOUT_ME: DS_AAM_SHOW_${tileData?.supportedTileId?.toUpperCase()}`,
          })
          handleClick(tileData)
        }}
        ref={tileRef}
        data-testid={tileData.id}
      >
        <TileText
          icon={tileData.icon}
          color={color}
          title={tileData.title}
          header={tileData.header}
          subHeading={tileData.additionalText}
        />
      </button>
      <TheaterOverlay
        showHelp={false}
        {...overlay}
        title={t('common-ui:back.action', 'Back')}
        helpActionUrl={helpActionUrl}
      >
        <div css={tileContentWrapperCss}>
          <Theme theme={themes.nightfall}>
            <TileContent tileData={tileData} highlightColor={color} />
          </Theme>
        </div>
      </TheaterOverlay>
      {openAncestorDetails && <AncestorDetails person={person} setOpenAncestorDetails={setOpenAncestorDetails} />}
    </>
  )
}

Tile.propTypes = {
  tileData: shape({
    header: string,
    icon: string,
    id: number,
    image: string,
    imageUrl: string,
    language: string,
    parsedNames: shape({}),
    region: string,
    title: string,
    type: string,
    year: oneOfType([string, number]),
  }),
  color: string,
  autoload: bool,
  index: number,
}
